export interface ParsedCollectedItem {
  category: string;
  sku: string;
  serialNumber: string | null;
  assetTag: string | null;
  quantity: number;
  warranty: {
    expiryDate: string | null;
    hasAdrWarranty: boolean;
    lifecyclePack: string;
  };
}

/**
 * Parses legacy stringified collection item names into strictly typed schema objects.
 * Handles patterns from Offboard, ADR, and Tech Refresh modals.
 */
export function parseCollectedItemString(
  inputName: string, 
  inputQuantity: number = 1
): ParsedCollectedItem {
  let category = inputName;
  let sku = "UNKNOWN";
  let serialNumber: string | null = null;
  let assetTag: string | null = null;
  let quantity = inputQuantity;

  // 1. Handle leading quantity prefixes (e.g., "1x Bag / Case", "2x Phone Case")
  const qtyMatch = inputName.match(/^(\d+)x\s+(.*)/i);
  if (qtyMatch) {
    quantity = parseInt(qtyMatch[1], 10);
    inputName = qtyMatch[2].trim();
    category = inputName;
  }

  // 2. Handle ADR format: "Desktop (Model: QC24251-CTO-GAP, SN: 2SMGPD4)"
  const adrMatch = inputName.match(/^(.*?)\s*\((.*?)\)$/);
  if (adrMatch) {
    category = adrMatch[1].trim();
    const internals = adrMatch[2];
    
    const parts = internals.split(',').map(p => p.trim());
    parts.forEach(part => {
      const lowerPart = part.toLowerCase();
      if (lowerPart.startsWith('model:')) {
        sku = part.substring(6).trim();
      } else if (lowerPart.startsWith('sn:')) {
        serialNumber = part.substring(3).trim();
      }
    });
    
    return { 
      category, sku, serialNumber, assetTag, quantity, 
      warranty: { expiryDate: null, hasAdrWarranty: false, lifecyclePack: "No" }
    };
  }

  // 3. Handle standard Offboard/Refresh format: "Laptop: RB14250-CTO-GAP, Serial: 5HG3F54, Tag: none"
  if (inputName.includes(':') || inputName.includes(',')) {
    const parts = inputName.split(',').map(p => p.trim());
    
    // The first segment is usually "Category: SKU" (e.g., "Laptop: RB14250-CTO-GAP")
    const firstPart = parts[0];
    const catSkuMatch = firstPart.match(/^(.*?):\s*(.*)$/);
    if (catSkuMatch) {
      category = catSkuMatch[1].trim();
      sku = catSkuMatch[2].trim();
    } else {
       category = firstPart; // Fallback if no colon exists
    }

    // Process remaining segments for Serial and Tag
    for (let i = 1; i < parts.length; i++) {
      const part = parts[i];
      const lowerPart = part.toLowerCase();
      
      if (lowerPart.startsWith('serial:')) {
         serialNumber = part.substring(7).trim();
         if (serialNumber.toLowerCase() === 'none') serialNumber = null;
      } else if (lowerPart.startsWith('tag:')) {
         assetTag = part.substring(4).trim();
         if (assetTag.toLowerCase() === 'none') assetTag = null;
      }
    }
  }

  // Fallback return for simple un-concatenated strings like "Bag / Case" or "Monitor"
  return { 
    category, sku, serialNumber, assetTag, quantity,
    warranty: { expiryDate: null, hasAdrWarranty: false, lifecyclePack: "No" }
  };
}
